import { Autocomplete, TextField } from '@mui/material';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { useMemo } from 'react';
import { FormValues } from './constants/orderForm.constants';

interface CostCenter {
  id: number;
  name: string;
}

interface Props {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  costCenters: CostCenter[];
  hasSingleCostCenter: boolean;
  isReadOnly: boolean;
}

export const OrderCostCenterField = ({
  control,
  errors,
  costCenters,
  hasSingleCostCenter,
  isReadOnly,
}: Props) => {
  const noCostCentersAvailable = costCenters.length === 0;

  const isDisabled =
    noCostCentersAvailable || hasSingleCostCenter || isReadOnly;

  const showError = Boolean(errors.costCenterId) || noCostCentersAvailable;

  const helperMessage = useMemo(() => {
    if (noCostCentersAvailable) {
      return 'No tiene centros de costos asignados';
    }

    if (hasSingleCostCenter) {
      return 'Centro de costo asignado automáticamente';
    }

    return errors.costCenterId?.message;
  }, [noCostCentersAvailable, hasSingleCostCenter, errors.costCenterId]);

  const costCentersById = useMemo(() => {
    const map = new Map<number, CostCenter>();

    for (const center of costCenters) {
      map.set(center.id, center);
    }

    return map;
  }, [costCenters]);

  return (
    <Controller
      control={control}
      name="costCenterId"
      rules={{
        required: noCostCentersAvailable
          ? false
          : 'Debe seleccionar un centro de costo',
      }}
      render={({ field }) => {
        let selectedOption: CostCenter | null = null;

        if (field.value !== null) {
          selectedOption = costCentersById.get(field.value) ?? null;
        }

        return (
          <Autocomplete
            options={costCenters}
            value={selectedOption}
            disabled={isDisabled}
            getOptionLabel={(option) => option.name}
            onChange={(_, value) => {
              if (value) {
                field.onChange(value.id);
                return;
              }

              field.onChange(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Centro de costo"
                error={showError}
                helperText={helperMessage}
              />
            )}
          />
        );
      }}
    />
  );
};
